import { useCallback, useEffect, useState } from 'react';
import { HandHeart, Undo2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import Ayuda from '../ui/Ayuda';
import { loQueFaltaParaPrestar } from './delegacionReglas';

// =====================================================================
// PRESTAR LA FIRMA SIN PRESTAR LA CONTRASEÑA · Escala Fase E3
// =====================================================================
// Esto no compite contra «esperar a que vuelva»: compite contra la
// contraseña prestada, que es lo que pasa de verdad cuando quien firma
// se va una semana. Y la contraseña prestada es el peor de los dos
// mundos — el control sigue puesto y ya no significa nada, porque el
// registro dice que firmó una persona que estaba en la playa.
//
// POR ESO LA PANTALLA TIENE QUE SER CÓMODA. Si estorba, se vuelve a la
// contraseña y no nos enteramos.

// Las reglas de qué falta para prestar viven en `delegacionReglas.js`,
// aparte y sin React, para poder compararlas con las del servidor sin
// montar la pantalla. Ver la cabecera de aquel archivo.

function hoyISO() {
  // DESDE HOY Y NUNCA UNA FECHA ESCRITA A MANO: la misma regla que las
  // pruebas de este repositorio.
  return new Date().toISOString().slice(0, 10);
}

function enDias(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function MiFirma() {
  const [prestadas, setPrestadas] = useState([]);
  const [recibidas, setRecibidas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [prestando, setPrestando] = useState(false);
  const [retirar, setRetirar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await api.get('/escala/delegaciones');
      setPrestadas(d.prestadas || []);
      setRecibidas(d.recibidas || []);
      setError('');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo cargar la lista. Revisa tu conexión y vuelve a entrar.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function confirmarRetirada() {
    try {
      const r = await api.del(`/escala/delegaciones/${retirar.id}`);
      setAviso(r?.mensaje || 'Retiraste la firma que le habías prestado.');
      setRetirar(null);
      await cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo retirar. Vuelve a intentarlo.');
    }
  }

  if (cargando) return <p className="p-6 text-gray-500">Cargando tus delegaciones…</p>;

  return (
    <div className="p-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-bold">Mi firma de autorización</h1>
        <Ayuda id="escala.mi_firma.pantalla" />
      </header>

      {aviso && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {aviso}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold">Lo que presté</h2>
          <button
            type="button"
            onClick={() => setPrestando(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold"
          >
            <HandHeart className="h-4 w-4" aria-hidden="true" />
            Prestar mi firma
          </button>
        </div>

        {prestadas.length === 0 ? (
          <EstadoVacio
            icono={HandHeart}
            titulo="No le has prestado tu firma a nadie"
            descripcion={'Si te vas unos días, aquí puedes dejar que otra persona '
              + 'autorice en tu lugar. Le prestas el visto bueno, no la llave: '
              + 'podrá firmar lo que otros pidan, no hacerlo por su cuenta.'}
          />
        ) : (
          <ul className="space-y-2">
            {prestadas.map((d) => (
              <li key={d.id} className="rounded-2xl border p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold">{d.suplente}</div>
                  <div className="text-sm text-gray-600">
                    {d.nombre_permiso || d.permiso}
                  </div>
                  <div className="text-sm text-gray-600">
                    Del {d.desde} al {d.hasta}
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{d.motivo}</p>
                </div>
                <div className="shrink-0 text-right space-y-2">
                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                    d.viva ? 'bg-green-50 text-green-700 border border-green-200'
                           : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
                  >
                    {d.viva ? 'Vigente' : d.revocada_en ? 'Retirada' : 'Terminada'}
                  </span>
                  {d.viva && (
                    <button
                      type="button"
                      onClick={() => setRetirar(d)}
                      className="block w-full rounded-xl border px-3 py-2 text-sm font-semibold"
                    >
                      <Undo2 className="inline h-4 w-4 mr-1" aria-hidden="true" />
                      Retirar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold">Lo que me prestaron</h2>
        {recibidas.length === 0 ? (
          <EstadoVacio
            icono={HandHeart}
            titulo="Nadie te ha prestado su firma"
            descripcion={'Cuando alguien lo haga, aquí verás qué puedes autorizar '
              + 'en su nombre y hasta qué día.'}
          />
        ) : (
          <ul className="space-y-2">
            {recibidas.map((d) => (
              <li key={d.id} className="rounded-2xl border p-4">
                <div className="font-semibold">{d.titular}</div>
                <div className="text-sm text-gray-600">
                  Te dejó autorizar: {d.nombre_permiso || d.permiso}
                </div>
                <div className="text-sm text-gray-600">
                  Del {d.desde} al {d.hasta}
                </div>
                {/* LA REGLA DURA, PEGADA A LO QUE LA EJECUTA: quien lee
                    esta línea es quien va a firmar en nombre de otro. */}
                <Ayuda id="escala.mi_firma.recibidas" />
                
              </li>
            ))}
          </ul>
        )}
      </section>

      {retirar && (
        <Modal titulo="Retirar la firma prestada" onCerrar={() => setRetirar(null)}>
          <p className="text-sm leading-relaxed">
            Desde ahora {retirar.suplente} ya no podrá autorizar en tu nombre.
            Lo que firmó antes sigue firmado.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={confirmarRetirada}
              className="rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold"
            >
              Sí, retirarla
            </button>
            <button
              type="button"
              onClick={() => setRetirar(null)}
              className="rounded-xl border px-4 py-2.5 font-semibold"
            >
              Mejor no
            </button>
          </div>
        </Modal>
      )}

      {prestando && (
        <FormularioPrestar
          onCerrar={() => setPrestando(false)}
          onPrestada={async (mensaje) => {
            setPrestando(false);
            setAviso(mensaje);
            await cargar();
          }}
        />
      )}
    </div>
  );
}

function FormularioPrestar({ onCerrar, onPrestada }) {
  const [gente, setGente] = useState([]);
  const [mios, setMios] = useState([]);   // [{codigo, nombre, descripcion}]
  const [aQuien, setAQuien] = useState('');
  const [permiso, setPermiso] = useState('');
  const [desde, setDesde] = useState(hoyISO());
  const [hasta, setHasta] = useState(enDias(7));
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // SOLO LO QUE UNO PUEDE APROBAR aparece en la lista, y con su
        // nombre en español. Ofrecer un permiso que el servidor va a
        // rechazar es hacer teclear algo para después decir que no.
        const [u, p] = await Promise.all([
          api.get('/usuarios'),
          api.get('/escala/lo-que-puedo-prestar'),
        ]);
        setGente((u?.items || u || []).filter((x) => x.activo !== false));
        setMios(p || []);
      } catch {
        setError('No se pudo cargar la lista de gente de tu empresa. '
          + 'Vuelve a entrar y prueba otra vez.');
      }
    })();
  }, []);

  const falta = loQueFaltaParaPrestar({
    aQuien, permiso, desde, hasta, motivo,
  });

  async function guardar() {
    setGuardando(true);
    try {
      await api.post('/escala/delegaciones', {
        id_suplente: Number(aQuien),
        permiso,
        desde,
        hasta,
        motivo: motivo.trim(),
      });
      await onPrestada('Le prestaste tu firma. Desde hoy puede autorizar en tu '
        + 'nombre hasta la fecha que pusiste, y puedes retirarla cuando '
        + 'quieras.');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo guardar. Vuelve a intentarlo.');
      setGuardando(false);
    }
  }

  return (
    <Modal titulo="Prestar mi firma" onCerrar={onCerrar}>
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-semibold">A quién</span>
          <select
            value={aQuien}
            onChange={(e) => setAQuien(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
          >
            <option value="">Elige a una persona…</option>
            {gente.map((g) => (
              <option key={g.id} value={g.id}>{g.nombre_completo}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-semibold">Qué le prestas</span>
          <select
            value={permiso}
            onChange={(e) => setPermiso(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
          >
            <option value="">Elige una autorización…</option>
            {mios.map((c) => (
              <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
            ))}
          </select>
          <Ayuda id="escala.mi_firma.que_prestas" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-semibold">Desde</span>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2.5"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">Hasta</span>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2.5"
            />
          </label>
        </div>
        <Ayuda id="escala.mi_firma.fechas" />

        <label className="block">
          <span className="text-sm font-semibold">Por qué</span>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
            placeholder="Vacaciones, viaje, incapacidad…"
          />
        </label>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={guardar}
            disabled={!!falta || guardando}
            title={falta || undefined}
            className="rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Prestar mi firma'}
          </button>
          <button type="button" onClick={onCerrar} className="rounded-xl border px-4 py-2.5 font-semibold">
            Cancelar
          </button>
          {falta && <span className="text-xs text-gray-600 basis-full">{falta}</span>}
        </div>
      </div>
    </Modal>
  );
}
